import { useEffect, useState, useRef, useCallback } from "react";
import "./index.css";
import { EmailSignature, generateSignatureHtml } from "../EmailSignature";
import TemplatePreviewModal from "../TemplatePreviewModal";
import DynamicVariablesPanel from "../Dynamic Variables";
import RichTextEditor from "../RichTextEditor";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { PrimaryButton } from "../../../StyledCss/styledbtn";
import toast from "react-hot-toast";
import { getDynamicVariables, addEmailTemplate, updateEmailTemplate } from "../../../api/services/hroneApi";

const TEMPLATE_TYPES = [
  { value: "", label: "Select Template Type" },
  { value: "invoice", label: "Invoice" },
  { value: "welcome", label: "Welcome" },
];


const defaultTemplate = {
  name: "",
  type: "",
  subject: "",
  body: "",
  signature: null,
  company_logo: null,
};

const parseSignatureHtml = (signatureHtml) => {
  if (!signatureHtml || typeof signatureHtml !== 'string') return null;
  
  const signature = {};
  
  // Extract image from img tag
  const imgMatch = signatureHtml.match(/<img[^>]+src="([^"]+)"/i);
  if (imgMatch) {
    signature.image = imgMatch[1];
  }
  
  // Extract email from mailto href
  const emailHrefMatch = signatureHtml.match(/mailto:([^"]+)"/i);
  if (emailHrefMatch) {
    signature.email = emailHrefMatch[1];
  }
    // Extract website from href
  const websiteHrefMatch = signatureHtml.match(/<a[^>]+href="(https?:\/\/[^"]+)"/i);
  if (websiteHrefMatch) {
    signature.website = websiteHrefMatch[1].replace(/^https?:\/\/(www\.)?/, "");
  }
  
  // Remove HTML tags and split by line breaks
  const text = signatureHtml.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  
  lines.forEach(line => {
    // Check for title | phone pattern
    if (line.includes('|') && !line.includes('@') && !line.includes('www')) {
      const parts = line.split('|').map(s => s.trim()).filter(s => s);
      if (parts.length >= 2) {
        signature.title = parts[0];
        signature.phone = parts[1];
      } else if (parts.length === 1) {
        signature.title = parts[0];
      }
    }
    else if (!signature.email && line.includes('@')) {
      const emailMatch = line.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
      if (emailMatch) {
        signature.email = emailMatch[0];
      }
    }
    // Check for website
    else if (!signature.website && line.includes("www."))  {
      signature.website = line.replace(/^www\./, '');
    }
    // Check for name (usually after "Thanks & Regards," and before title)
    else if (line && !line.includes('Thanks') && !line.includes('Regards') && !signature.name && lines.indexOf(line) > 0) {
      // Likely the name if it's not a number and comes early
      if (!/^\d+$/.test(line)) {
        signature.name = line;
      }
    }
  });
  
  return Object.keys(signature).length > 0 ? signature : null;
};

export default function EmailTemplateForm({
  template,
  onSave = (data) => {
    toast.success("Template saved successfully!");
  },
  onCancel = () => {
    window.history.back();
  },
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { emailTemplate, isEditMode } = location.state || {};
  const [dynamicVaribles,setDynamicVaribles]=useState([])
  const [formData, setFormData] = useState(defaultTemplate);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const bodyEditorRef = useRef(null);
  const subjectInputRef = useRef(null);
  const [lastFocusedField, setLastFocusedField] = useState('body');
  const savedRangeRef = useRef(null);
  const signatureFieldRefs = useRef({});
  const initialFormDataRef = useRef(null);
   const [loadingStates, setLoadingStates] = useState({
    addEmailTemplate: false,
    updateEmailTemplate: false,
  });

  const setLoading = (operation, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
  };

  useEffect(() => {


    const templateData = emailTemplate || template;
    if (templateData) {
      const parsedSignature = typeof templateData.signature === 'string' 
        ? parseSignatureHtml(templateData.signature) 
        : templateData.signature;
      
      const initialData = {
        name: templateData.templateName || "",
        type: templateData.templateType || "",
        subject: templateData.subject || "",
        body: templateData.emailBody || "",
        signature: parsedSignature,
        company_logo: parsedSignature?.image || null,
      };
      setFormData(initialData);
      initialFormDataRef.current = initialData;
    } else {
      setFormData(defaultTemplate);
      initialFormDataRef.current = defaultTemplate;
    }
    setErrors({});
  }, [template, emailTemplate]);
  const handleBackTemplateDashboard = () => {
    const hasUnsavedChanges = initialFormDataRef.current && 
      JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
    
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm("You have unsaved changes! Do you really want to leave?");
      if (!confirmLeave) return;
    }
    navigate(-1);
  };
    const postEmailTemplate = useCallback(
      async (newEmailTemplate) => {
        try {
                  setLoading("addEmailTemplate", true);

  
          const response = await addEmailTemplate(newEmailTemplate);
          if (response.ok) {
            toast.success("Email Template Added Successfully");
            setFormData(defaultTemplate);
            setResetKey(prev => prev + 1);
          } else {
            toast.error("Email Template cannot be Added");
          }
        } catch (error) {
            toast.error("Email Template cannot be Added");
          console.error("Error adding Email Template:", error);
        } 
        finally {
        setLoading("addEmailTemplate", false);
      }
      },
      [],
    );
    const handleUpdateEmailTemplate = useCallback(
        async (updateData) => {
          try {
                              setLoading("updateEmailTemplate", true);

            const response = await updateEmailTemplate(updateData);
            if (response.ok) {
              toast.success("Email Template Updated Successfully");
              initialFormDataRef.current = formData;
              setResetKey(prev => prev + 1);
            } else {
              toast.error("Failed to update Email Template");
            }
          } catch (error) {
            toast.error("Failed to update Email Template");
            console.error("Error updating Email Template:", error);
          } finally {
        setLoading("updateEmailTemplate", false);
      }
        },
        [formData],
      );
  const DynamicVariables = useCallback(async () => {
    try {
      const response = await getDynamicVariables();
      const Variables = await response.json();
      setDynamicVaribles(Variables.data)
    } catch {
      
    }
  }, []);
  
  useEffect(()=>{DynamicVariables()},[DynamicVariables])

  const handleInsertVariable = (variableText) => {
    if (lastFocusedField === 'subject' && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const text = input.value;
      
      const newValue = text.substring(0, start) + variableText + text.substring(end);
      setFormData((p) => ({ ...p, subject: newValue }));
      
      setTimeout(() => {
        input.focus();
        const newPos = start + variableText.length;
        input.setSelectionRange(newPos, newPos);
      }, 0);
    } else if (lastFocusedField.startsWith('signature-')) {
      const fieldName = lastFocusedField.replace('signature-', '');
      const input = signatureFieldRefs.current[fieldName];
      
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const text = input.value || '';
        
        const newValue = text.substring(0, start) + variableText + text.substring(end);
        setFormData((p) => ({
          ...p,
          signature: { ...p.signature, [fieldName]: newValue }
        }));
        
        setTimeout(() => {
          input.focus();
          const newPos = start + variableText.length;
          input.setSelectionRange(newPos, newPos);
        }, 0);
      }
    } else if (bodyEditorRef.current && savedRangeRef.current) {
      const editor = bodyEditorRef.current;
      const selection = window.getSelection();
      
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
      
      const range = selection.getRangeAt(0);
      const textNode = document.createTextNode(variableText);
      range.insertNode(textNode);
      
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
      
      savedRangeRef.current = range.cloneRange();
      editor.focus();
      
      setFormData((p) => ({ ...p, body: editor.innerHTML }));
    }
  };

  const validate = () => {
    const err = {};
    if (!formData.name.trim()) err.name = "Template name is required";
    if (!formData.type.trim()) err.type = "Template type is required";
    if (!formData.subject.trim()) err.subject = "Email subject is required";
    if (!formData.body || formData.body === "<br>")
      err.body = "Email body is required";
    
    if (!formData.signature?.name?.trim()) err.signatureName = "Full name is required";
    if (!formData.signature?.title?.trim()) err.signatureTitle = "Job title is required";
    
    const isDynamicVariable = (value) => /^\{\{.*\}\}$/.test(value?.trim());
    
    if (!formData.signature?.email?.trim()) {
      err.signatureEmail = "Email is required";
    } else if (!isDynamicVariable(formData.signature.email)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.signature.email.trim())) {
        err.signatureEmail = "Invalid email format";
      }
    }
    
    if (!formData.signature?.phone?.trim()) {
      err.signaturePhone = "Phone is required";
    } else if (!isDynamicVariable(formData.signature.phone)) {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(formData.signature.phone.trim())) {
        err.signaturePhone = "Invalid phone number format";
      }
    }
    
    if (!formData.signature?.website?.trim()) {
      err.signatureWebsite = "Website is required";
    } else if (!isDynamicVariable(formData.signature.website)) {
      const websiteRegex = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:[0-9]+)?(\/.*)?$/;
      if (!websiteRegex.test(formData.signature.website.trim())) {
        err.signatureWebsite = "Invalid website format";
      }
    }
    
    if (!formData.company_logo) err.signatureLogo = "Company logo is required";
    
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const hasUnsavedChanges = initialFormDataRef.current && 
    JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);

  const handleSave = () => {
    if (!validate()) {
      toast.error("Please fill all the mandatory feilds");
      return;
    }

    const signatureHtml = formData.signature ? generateSignatureHtml({ ...formData.signature, logoUrl: formData.company_logo }) : (formData.company_logo ? generateSignatureHtml({ logoUrl: formData.company_logo }) : null);

    const templateData = {
      template_name: formData.name,
      template_type: formData.type,
      subject: formData.subject,
      email_body: formData.body,
      signature: signatureHtml,
    };

    if (isEditMode && emailTemplate?.id) {
      handleUpdateEmailTemplate({ ...templateData, id: emailTemplate.id });
    } else {
      postEmailTemplate(templateData);
    }
  };

  return (
    <div>
      
      <Header />

      <div className="d-flex">
        <LeftNavBar />

        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>
              <ArrowLeft size={20} 
              onClick={() => handleBackTemplateDashboard()}
              style={{ cursor: "pointer" }} /> Email Template
            </PageHeaderName>
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              <PrimaryButton onClick={handleSave} disabled={loadingStates.addEmailTemplate || loadingStates.updateEmailTemplate || (isEditMode && !hasUnsavedChanges)}>
                {isEditMode 
                  ? (loadingStates.updateEmailTemplate ? "Updating..." : "Update Template")
                  : (loadingStates.addEmailTemplate ? "Saving..." : "Save Template")}
              </PrimaryButton>
              <PrimaryButton onClick={() => setShowPreview(true)}>
                Preview
              </PrimaryButton>
            </div>
          </PageHeader>
          <div
            style={{ 
              padding: "16px", 
              display: "flex", 
              gap: "60px", 
              maxWidth: "100%", 
              overflow: "hidden", 
              flexWrap:"wrap"
            }}
          >
            {/* Main Form */}
            <div >
              {/* Template Details */}
              <div className="card mb-4 shadow-sm">
                <div className="card-header">
                  <strong>Template Details</strong>
                </div>

                <div className="card-body">
                  <div className="row g-3">
                    {/* Name */}
                    <div className="col-md-6">
                      <label className="form-label">
                        Template Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        value={formData.name}
                        onChange={(e) => {
                          setFormData((p) => ({ ...p, name: e.target.value }));
                        }}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>

                    {/* Type */}
                    <div className="col-md-6">
                      <label className="form-label">Template Type <span className="text-danger">*</span></label>
                      <select
                        className={`form-select ${
                          errors.type ? "is-invalid" : ""
                        }`}
                        value={formData.type}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, type: e.target.value }))
                        }
                      >
                        {TEMPLATE_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      {errors.type && (
                        <div className="invalid-feedback">{errors.type}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Subject */}
              <div className="card mb-4 shadow-sm">
                <div className="card-header">
                  <strong>Email Subject</strong>
                </div>
                <div className="card-body">
                  <label className="form-label">
                    Subject <span className="text-danger">*</span>
                  </label>
                  <input
                    ref={subjectInputRef}
                    className={`form-control ${
                      errors.subject ? "is-invalid" : ""
                    }`}
                   style={{ width: "100%" }}

                    value={formData.subject}
                    onFocus={() => setLastFocusedField('subject')}
                    onClick={() => setLastFocusedField('subject')}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        subject: e.target.value,
                      }))
                    }
                  />
                  {errors.subject && (
                    <div className="invalid-feedback">{errors.subject}</div>
                  )}
                </div>
              </div>

              {/* Email Body */}
              <div className="card shadow-sm">
                <div className="card-header">
                  <strong>Email Body</strong> <span className="text-danger">*</span>
                </div>
                <div className="card-body">
                  <RichTextEditor
                    value={formData.body}
                    onChange={(val) =>
                      setFormData((p) => ({ ...p, body: val }))
                    }
                    onEditorReady={(ref) => {
                      bodyEditorRef.current = ref.current;
                    }}
                    onFocus={() => setLastFocusedField('body')}
                    onSelectionChange={(range) => {
                      savedRangeRef.current = range;
                    }}
                  />
                  {errors.body && (
                    <div className="text-danger small mt-2">
                      {errors.body}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div style={{ width: "350px", flexShrink: 0 }}>
              <EmailSignature
                key={resetKey}
                signature={formData.signature}
                companyLogo={formData.company_logo}
                errors={errors}
                onChange={(sig) =>
                  setFormData((p) => ({ ...p, signature: sig }))
                }
                onLogoChange={(logoData) => setFormData((p) => ({ ...p, company_logo: logoData }))}
                onFieldFocus={(fieldName) => {
                  setLastFocusedField(fieldName);
                  setTimeout(() => {
                    const fieldKey = fieldName.replace('signature-', '');
                    const inputs = document.querySelectorAll('.card input.form-control');
                    inputs.forEach(input => {
                      if (input === document.activeElement) {
                        signatureFieldRefs.current[fieldKey] = input;
                      }
                    });
                  }, 0);
                }}
              />
              {errors.signature && (
                <div className="text-danger small mt-2">
                  {errors.signature}
                </div>
              )}
              <div className="mt-4">
                <DynamicVariablesPanel 
                  templateType={formData.type}
                  onInsertVariable={handleInsertVariable}
                  dynamicVariables={dynamicVaribles}
                />
              </div>
            </div>

            <TemplatePreviewModal
              open={showPreview}
              onOpenChange={setShowPreview}
              template={formData}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
